import CalendarIcon from "../../../assets/icons/CalendarIcon";
import ChevronDownIcon from "../../../assets/icons/ChevronDownIcon";
import ResetIcon from "../../../assets/icons/ResetIcon";
import React from "react";
import DatePicker from "react-datepicker";

type DateFieldProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  value?: string;
  placeholder: string;
  hasValue: boolean;
  displayValue?: string;
  emptyValue?: string;
};

const DateField = React.forwardRef<HTMLButtonElement, DateFieldProps>(
  (
    {
      onClick,
      placeholder,
      hasValue,
      displayValue,
      value,
      emptyValue,
      className,
      ...rest
    },
    ref,
  ) => {
    const mergedClassName = ["transactions-filter__field", className]
      .filter(Boolean)
      .join(" ");
    const resolvedValue = displayValue || value || "";
    const fallbackValue = emptyValue || placeholder;

    return (
      <button
        type="button"
        className={mergedClassName}
        data-empty={hasValue ? "false" : "true"}
        onClick={onClick}
        ref={ref}
        {...rest}
      >
        <span className="transactions-filter__icon" aria-hidden="true">
          <CalendarIcon />
        </span>
        <span className="transactions-filter__text">
          <span className="transactions-filter__label">{placeholder}</span>
          <span className="transactions-filter__value">
            {hasValue ? resolvedValue : fallbackValue}
          </span>
        </span>
      </button>
    );
  },
);

DateField.displayName = "DateField";

type Filters = {
  from: Date | null;
  to: Date | null;
  type: string;
};

type TransactionsFilterPanelProps = {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  hasActiveFilters: boolean;
  hasPendingChanges: boolean;
  dateRangeLabel: string;
  resetLabel: string;
  applyLabel: string;
  fromLabel: string;
  toLabel: string;
  typeLabel: string;
  selectTypeLabel: string;
  typeOptions: string[];
  formatDate: (value?: string | number | Date) => string;
  onApply: () => void;
};

const TransactionsFilterPanel = React.forwardRef<
  HTMLDivElement,
  TransactionsFilterPanelProps
>(
  (
    {
      filters,
      setFilters,
      hasActiveFilters,
      hasPendingChanges,
      dateRangeLabel,
      resetLabel,
      applyLabel,
      fromLabel,
      toLabel,
      typeLabel,
      selectTypeLabel,
      typeOptions,
      formatDate,
      onApply,
    },
    ref,
  ) => {
    const today = React.useMemo(() => {
      const currentDate = new Date();
      currentDate.setHours(23, 59, 59, 999);
      return currentDate;
    }, []);

    return (
      <div
        className="transactions-filter"
        id="transactions-filter-panel"
        ref={ref}
      >
        <div className="transactions-filter__header">
          <div className="transactions-filter__title">{dateRangeLabel}</div>
          <button
            type="button"
            className="transactions-filter__reset"
            onClick={() =>
              setFilters({
                from: null,
                to: null,
                type: "",
              })
            }
            disabled={!hasActiveFilters}
          >
            <ResetIcon />
            <span>{resetLabel}</span>
          </button>
        </div>
        <div className="transactions-filter__dates">
          <DatePicker
            selected={filters.from}
            onChange={(date) =>
              setFilters((prev) => ({
                ...prev,
                from: date,
              }))
            }
            startDate={filters.from}
            endDate={filters.to}
            selectsStart
            maxDate={filters.to ?? today}
            dateFormat="dd MMM yyyy"
            customInput={
              <DateField
                placeholder={fromLabel}
                hasValue={Boolean(filters.from)}
                emptyValue={fromLabel}
                displayValue={filters.from ? formatDate(filters.from) : ""}
                aria-label={fromLabel}
              />
            }
            popperClassName="transactions-datepicker-popper"
            calendarClassName="transactions-datepicker"
          />
          <DatePicker
            selected={filters.to}
            onChange={(date) =>
              setFilters((prev) => ({
                ...prev,
                to: date,
              }))
            }
            startDate={filters.from}
            endDate={filters.to}
            selectsEnd
            minDate={filters.from ?? undefined}
            maxDate={today}
            dateFormat="dd MMM yyyy"
            customInput={
              <DateField
                placeholder={toLabel}
                hasValue={Boolean(filters.to)}
                emptyValue={toLabel}
                displayValue={filters.to ? formatDate(filters.to) : ""}
                aria-label={toLabel}
              />
            }
            popperClassName="transactions-datepicker-popper"
            calendarClassName="transactions-datepicker"
          />
        </div>
        <div className="transactions-filter__type">
          <div className="transactions-filter__label">{typeLabel}</div>
          <div
            className="transactions-filter__select"
            data-placeholder={filters.type ? "false" : "true"}
          >
            <select
              value={filters.type}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  type: event.target.value,
                }))
              }
              aria-label={selectTypeLabel}
            >
              <option value="">{selectTypeLabel}</option>
              {typeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span className="transactions-filter__chevron" aria-hidden="true">
              <ChevronDownIcon />
            </span>
          </div>
        </div>
        <div className="transactions-filter__actions">
          <button
            type="button"
            className="transactions-filter__apply"
            onClick={onApply}
            disabled={!hasPendingChanges}
          >
            {applyLabel}
          </button>
        </div>
      </div>
    );
  },
);

TransactionsFilterPanel.displayName = "TransactionsFilterPanel";

export default TransactionsFilterPanel;
